import {
  ILayerRepository,
  LayerEntity,
  GeometryTypeEnum,
  LayerTypeEnum,
  ResponseValueObject,
  LegendItemValueObject,
  LayerValueObject,
  ArcgisEndpointConstant,
} from '@gishub/domain';
import axios from 'axios';

export class LayerRepository implements ILayerRepository {

  private static readonly rawItems: (Partial<Omit<LayerEntity, 'layers'>> & { layers?: any[] })[] = [
    {
      name: "Cartografía Base",
      type: LayerTypeEnum.Group,
      visible: true,
      layers: [
        {
          name: "Límite departamental",
          geometryType: GeometryTypeEnum.Polygon,
          referenceSource: "MIDAGRI",
          // Límites políticos servidos por SERV_LIMITE_CENSAL (winlmprap14): campos CODDEP/CODPROV/UBIGEO.
          url: ArcgisEndpointConstant.urlOf(ArcgisEndpointConstant.LIMITE_DEPARTAMENTO),
          type: LayerTypeEnum.MapServer,
          visible: true,
          disabled: false,
          hasProxy: false,
          popupEnabled: false,
          labelsVisible: false,
        },
        {
          name: "Límite provincial",
          geometryType: GeometryTypeEnum.Polygon,
          referenceSource: "MIDAGRI",
          url: ArcgisEndpointConstant.urlOf(ArcgisEndpointConstant.LIMITE_PROVINCIAL),
          type: LayerTypeEnum.MapServer,
          visible: false,
          disabled: false,
          hasProxy: false,
          opacity: 0.65
        },
        {
          name: "Límite distrital",
          geometryType: GeometryTypeEnum.Polygon,
          referenceSource: "MIDAGRI",
          url: ArcgisEndpointConstant.urlOf(ArcgisEndpointConstant.LIMITE_DISTRITAL),
          type: LayerTypeEnum.MapServer,
          visible: false,
          disabled: false,
          hasProxy: false,
          opacity: 0.7
        },
        {
          name: "Centros poblados",
          abbreviation: "CCPP",
          displayField: "NOMBCCPP",
          geometryType: GeometryTypeEnum.Point,
          referenceSource: "INEI",
          url: ArcgisEndpointConstant.urlOf(ArcgisEndpointConstant.CENTROS_POBLADOS),
          type: LayerTypeEnum.FeatureServer,
          visible: false,
          definitionQuery: "NOMBDEP LIKE '%{0}%'",
          definitionQueryWithLabel: true,
          disabled: false,
          hasProxy: false,
          opacity: 0.95,
          // 94 927 centros poblados (INEI 2021): inviable un símbolo por feature → densidad
          // ponderada por viviendas 2017 (campo VIV2017 del servicio).
          heatmapPoints: true,
          heatmapField: "VIV2017",
          // El heatmap dibuja todos los puntos visibles, así que sin esto la capa bajaría el
          // servicio con `outFields: ['*']`: el payload de 94 927 features crece con todos los
          // campos del centro poblado para descartar casi todos. Solo hacen falta el campo que
          // pondera la densidad y el del título del popup.
          // NOMBDEP no se pide: `definitionQuery` la resuelve el servidor, no el cliente.
          queryFields: ["VIV2017", "NOMBCCPP"]
        }
      ]
    },
    {
      name: "Elementos expuestos",
      type: LayerTypeEnum.Group,
      visible: false,
      layers: [
        {
          name: "Predio rural catastral",
          geometryType: GeometryTypeEnum.Polygon,
          referenceSource: "MIDAGRI",
          url: ArcgisEndpointConstant.urlOf(ArcgisEndpointConstant.PREDIO_RURAL_CATASTRAL),
          type: LayerTypeEnum.MapServer,
          visible: false,
          disabled: false,
          hasProxy: false,
          opacity: 0.6
        },
        {
          name: "Parcelas con cultivo",
          geometryType: GeometryTypeEnum.Polygon,
          referenceSource: "MIDAGRI",
          url: ArcgisEndpointConstant.urlOf(ArcgisEndpointConstant.PARCELAS_CULTIVO),
          type: LayerTypeEnum.MapServer,
          visible: false,
          disabled: false,
          hasProxy: false,
        },
        {
          name: "Productores agrícolas empadronados",
          geometryType: GeometryTypeEnum.Polygon,
          referenceSource: "MIDAGRI",
          // PADRON_GEO del servicio temporal2 (vivo).
          url: ArcgisEndpointConstant.urlOf(ArcgisEndpointConstant.PRODUCTORES_AGRICOLAS),
          type: LayerTypeEnum.MapServer,
          visible: false,
          disabled: false,
          hasProxy: false,
        },
        {
          name: "Productores pecuarios empadronados",
          geometryType: GeometryTypeEnum.Polygon,
          referenceSource: "MIDAGRI",
          // PADRON_PECUARIOS del servicio temporal2 (vivo).
          url: ArcgisEndpointConstant.urlOf(ArcgisEndpointConstant.PRODUCTORES_PECUARIOS),
          type: LayerTypeEnum.MapServer,
          visible: false,
          disabled: false,
          hasProxy: false,
        },
      ]
    },
    {
      name: "Peligros",
      type: LayerTypeEnum.Group,
      // El grupo va encendido para que su única capa activa por defecto
      // ("Incendios forestales") se dibuje: el TOC nunca enciende un hijo sin
      // encender su grupo, así que un grupo apagado dejaría el KPI del header
      // sin nada que contar en el mapa. El resto de hijos siguen apagados.
      visible: true,
      layers: [
        {
          name: "Puntos Críticos Priorizados 2026",
          geometryType: GeometryTypeEnum.Point,
          referenceSource: "ANA / SNIRH",
          url: ArcgisEndpointConstant.urlOf(ArcgisEndpointConstant.PC_PUNTOS_CRITICOS_INICIAL_2026),
          type: LayerTypeEnum.FeatureServer,
          visible: false,
          filterWithGeometry: true,
          disabled: false,
          hasProxy: false,
          clusterPoints: true,
          // 536 puntos críticos: se agrupan hasta 1:100 000 (vista nacional) y al acercarse
          // se resuelven en los puntos individuales con su simbología original.
          clusterMaxScale: 100_000,
          // Resumen de la zona agrupada (`FeatureReductionCluster.fields`): la etiqueta del
          // cluster muestra el conteo + POB, y el popup suma los habitantes atendidos.
          clusterAggregates: [
            { field: "POB", statisticType: "sum", alias: "Población", labelPrefix: "POB" },
            { field: "HABITANTES_ATENDIDOS", statisticType: "sum", alias: "Habitantes atendidos", labelPrefix: "Atend." },
          ],
          displayField: "NOMBRE",
        },
        {
          name: "Puntos Críticos Final Priorizados 2026",
          geometryType: GeometryTypeEnum.Point,
          referenceSource: "ANA / SNIRH",
          url: ArcgisEndpointConstant.urlOf(ArcgisEndpointConstant.PC_PUNTOS_CRITICOS_FINAL_2026),
          type: LayerTypeEnum.FeatureServer,
          visible: false,
          filterWithGeometry: true,
          disabled: false,
          hasProxy: false,
          clusterPoints: true,
          // Mismo criterio que la capa inicial: agrupado a escala nacional, puntos
          // individuales al acercarse más de 1:100 000.
          clusterMaxScale: 100_000,
          clusterAggregates: [
            { field: "POB", statisticType: "sum", alias: "Población", labelPrefix: "POB" },
            { field: "HABITANTES_ATENDIDOS", statisticType: "sum", alias: "Habitantes atendidos", labelPrefix: "Atend." },
          ],
          displayField: "NOMBRE",
        },
        {
          name: "Tramos Puntos Críticos Priorizados 2026",
          geometryType: GeometryTypeEnum.Line,
          referenceSource: "ANA / SNIRH",
          url: ArcgisEndpointConstant.urlOf(ArcgisEndpointConstant.PC_PUNTOS_CRITICOS_TRAMOS_2026),
          type: LayerTypeEnum.FeatureServer,
          visible: false,
          filterWithGeometry: true,
          disabled: false,
          hasProxy: false,
          animatedFlow: true,
          displayField: "NOMBRE",
        },
        {
          name: "Barreras Dinámicas D.U. 008-2026",
          geometryType: GeometryTypeEnum.Point,
          referenceSource: "ANA / SNIRH",
          url: ArcgisEndpointConstant.urlOf(ArcgisEndpointConstant.PC_BARRERAS_DINAMICAS_2026),
          type: LayerTypeEnum.FeatureServer,
          visible: false,
          filterWithGeometry: true,
          disabled: false,
          hasProxy: false,
          // Solo 12 puntos (D.U. N° 008-2026) repartidos en un puñado de quebradas:
          // agruparlos escondería el detalle, así que se dibujan individualmente y cada
          // punto se etiqueta con el nombre de su quebrada desde el cliente (`labelField`).
          labelField: "QUEBRADA",
          labelMinScale: 250_000,
          displayField: "QUEBRADA",
        },
        {
          name: "Última Posición de Maquinaria 2026",
          geometryType: GeometryTypeEnum.Point,
          referenceSource: "ANA / SNIRH",
          url: ArcgisEndpointConstant.urlOf(ArcgisEndpointConstant.PC_ULTIMA_POSICION_MAQUINARIA_2026),
          type: LayerTypeEnum.FeatureServer,
          visible: false,
          filterWithGeometry: true,
          disabled: false,
          hasProxy: false,
          clusterPoints: true,
          clusterMaxScale: 100_000,
          // Estado predominante de la maquinaria agrupada (`statisticType: 'mode'` sobre un
          // campo de texto): resume la burbuja sin abrir el popup. La marca va solo en el
          // popup (`inLabel: false`) porque alargaría demasiado la etiqueta.
          clusterAggregates: [
            { field: "ESTADO", statisticType: "mode", alias: "Estado predominante", labelPrefix: "Estado", numeric: false },
            { field: "MARCA", statisticType: "mode", alias: "Marca predominante", numeric: false, inLabel: false },
          ],
          displayField: "CODIGO_FICHA",
        },
        {
          name: "Ríos y Quebradas ANA (PC 2026)",
          geometryType: GeometryTypeEnum.Line,
          referenceSource: "ANA / SNIRH",
          url: ArcgisEndpointConstant.urlOf(ArcgisEndpointConstant.PC_RIOS_QUEBRADAS_2026),
          type: LayerTypeEnum.FeatureServer,
          visible: false,
          filterWithGeometry: false,
          disabled: false,
          hasProxy: false,
          animatedFlow: true,
          displayField: "NOMBRE_CA",
        },
        {
          name: "Unidades Hidrográficas ANA (PC 2026)",
          geometryType: GeometryTypeEnum.Polygon,
          referenceSource: "ANA / SNIRH",
          url: ArcgisEndpointConstant.urlOf(ArcgisEndpointConstant.PC_UNIDADES_HIDROGRAFICAS_2026),
          type: LayerTypeEnum.FeatureServer,
          visible: false,
          filterWithGeometry: false,
          disabled: false,
          hasProxy: false,
          displayField: "NOMBRE",
        },
        {
          name: "Departamentos en Emergencia 2026",
          geometryType: GeometryTypeEnum.Polygon,
          referenceSource: "ANA / SNIRH",
          url: ArcgisEndpointConstant.urlOf(ArcgisEndpointConstant.PC_DEPARTAMENTOS_EMERGENCIA_2026),
          type: LayerTypeEnum.FeatureServer,
          visible: false,
          filterWithGeometry: true,
          disabled: false,
          hasProxy: false,
          displayField: "DEPARTAMEN",
        },
        {
          name: "Lagunas glaciares con Riesgos de Desborde",
          geometryType: GeometryTypeEnum.Polygon,
          referenceSource: "GLACIER LAKE",
          url: "https://services6.arcgis.com/c9GFTG11debu0wsH/ArcGIS/rest/services/LGRD2024_/FeatureServer/0",
          type: LayerTypeEnum.FeatureServer,
          visible: false,
          filterWithGeometry: true,
          disabled: false,
          hasProxy: false,
        },
        {
          name: "Áreas inundadas Piura 12/02/2017",
          geometryType: GeometryTypeEnum.Polygon,
          referenceSource: "ANA",
          url: ArcgisEndpointConstant.urlOf(ArcgisEndpointConstant.AREAS_INUNDADAS_PIURA_12022017),
          type: LayerTypeEnum.FeatureServer,
          visible: false,
          filterWithGeometry: true,
          disabled: false,
          hasProxy: false,
        },
        {
          name: "Áreas inundadas Piura 19/01/2017",
          geometryType: GeometryTypeEnum.Polygon,
          referenceSource: "ANA",
          url: ArcgisEndpointConstant.urlOf(ArcgisEndpointConstant.AREAS_INUNDADAS_PIURA_19012017),
          type: LayerTypeEnum.FeatureServer,
          visible: false,
          filterWithGeometry: true,
          disabled: false,
          hasProxy: false,
        },
        {
          name: "Áreas inundadas Piura 20/03/2017",
          geometryType: GeometryTypeEnum.Polygon,
          referenceSource: "ANA",
          url: ArcgisEndpointConstant.urlOf(ArcgisEndpointConstant.AREAS_INUNDADAS_PIURA_20032017),
          type: LayerTypeEnum.FeatureServer,
          visible: false,
          filterWithGeometry: true,
          disabled: false,
          hasProxy: false,
        },
        {
          name: "Áreas inundadas Piura 23/03/2017",
          geometryType: GeometryTypeEnum.Polygon,
          referenceSource: "ANA",
          url: ArcgisEndpointConstant.urlOf(ArcgisEndpointConstant.AREAS_INUNDADAS_PIURA_23032017),
          type: LayerTypeEnum.FeatureServer,
          visible: false,
          filterWithGeometry: true,
          disabled: false,
          hasProxy: false,
        },
        {
          name: "Áreas inundadas Tumbes 15/04/2023",
          geometryType: GeometryTypeEnum.Polygon,
          referenceSource: "ANA",
          url: ArcgisEndpointConstant.urlOf(ArcgisEndpointConstant.AREAS_INUNDADAS_TUMBES_15042023),
          type: LayerTypeEnum.FeatureServer,
          visible: false,
          filterWithGeometry: true,
          disabled: false,
          hasProxy: false,
        },
        {
          name: "Incendios forestales",
          geometryType: GeometryTypeEnum.Point,
          referenceSource: "SERFOR",
          url: ArcgisEndpointConstant.urlOf(ArcgisEndpointConstant.INCENDIO_FORESTAL),
          type: LayerTypeEnum.MapServer,
          // Encendida por defecto: es el peligro que el KPI del header resume, así
          // que el número y lo que se ve en el mapa deben contarse de un vistazo.
          visible: true,
          filterWithGeometry: false,
          disabled: false,
          hasProxy: false,
          displayField: "FUENTE",
        },
        {
          name: "Focos de calor (últimas 24 h)",
          geometryType: GeometryTypeEnum.Point,
          referenceSource: "SERFOR",
          url: ArcgisEndpointConstant.urlOf(ArcgisEndpointConstant.FOCOS_CALOR_24H),
          type: LayerTypeEnum.MapServer,
          visible: false,
          filterWithGeometry: false,
          disabled: false,
          hasProxy: false,
        },
      ]
    },
    {
      name: "Otros",
      type: LayerTypeEnum.Group,
      visible: false,
      layers: [
        {
          name: "Sectores estadísticos",
          geometryType: GeometryTypeEnum.Polygon,
          referenceSource: "MIDAGRI",
          url: ArcgisEndpointConstant.urlOf(ArcgisEndpointConstant.SECTORES_ESTADISTICOS),
          type: LayerTypeEnum.MapServer,
          visible: false,
          disabled: false,
          hasProxy: false,
        },
        {
          name: "Juntas de usuario",
          geometryType: GeometryTypeEnum.Polygon,
          referenceSource: "MIDAGRI",
          url: ArcgisEndpointConstant.urlOf(ArcgisEndpointConstant.JUNTAS_USUARIO),
          type: LayerTypeEnum.MapServer,
          visible: false,
          disabled: false,
          hasProxy: false,
        },
        {
          name: "Comités de riego",
          geometryType: GeometryTypeEnum.Polygon,
          referenceSource: "MIDAGRI",
          url: ArcgisEndpointConstant.urlOf(ArcgisEndpointConstant.COMITES_RIEGO),
          type: LayerTypeEnum.MapServer,
          visible: false,
          disabled: false,
          hasProxy: false,
        },
        {
          name: "Agencias agrarias",
          abbreviation: "AGAGR",
          displayField: "AGENCIA_AG",
          geometryType: GeometryTypeEnum.Point,
          referenceSource: "MIDAGRI",
          url: ArcgisEndpointConstant.urlOf(ArcgisEndpointConstant.AGENCIAS_AGRARIAS),
          type: LayerTypeEnum.MapServer,
          visible: false,
          disabled: false,
          hasProxy: false,
        },
        {
          name: "Centros empadronados",
          geometryType: GeometryTypeEnum.Point,
          referenceSource: "MIDAGRI",
          url: ArcgisEndpointConstant.urlOf(ArcgisEndpointConstant.CENTROS_EMPADRONADOS),
          type: LayerTypeEnum.MapServer,
          visible: false,
          disabled: false,
          hasProxy: false,
        },
      ]
    },
  ];

  private static slugify(name: string): string {
    return name
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  private buildTree(): LayerEntity[] {
    return LayerRepository.rawItems
      .map(item => {
        if (item.layers) {
          const groupId = item.id ?? LayerRepository.slugify(item.name ?? 'group');
          const activeLayers = item.layers
            .filter(sub => !sub.disabled)
            .map((sub, i) => new LayerEntity({
              id: sub.id ?? `${groupId}-${LayerRepository.slugify(sub.name ?? 'layer')}-${i}`,
              ...sub,
            }));
          if (activeLayers.length === 0) return null;
          return new LayerEntity({ ...item, id: groupId, layers: activeLayers });
        }
        if (item.disabled) return null;
        const leafId = item.id ?? LayerRepository.slugify(item.name ?? 'layer');
        return new LayerEntity({ ...item, id: leafId });
      })
      .filter((x): x is LayerEntity => x != null);
  }

  async getAll(): Promise<ResponseValueObject<LayerValueObject>> {
    return new ResponseValueObject(true, new LayerValueObject('', this.buildTree()));
  }

  async getByProfile(_profileId: number): Promise<ResponseValueObject<LayerValueObject>> {
    return this.getAll();
  }

  async getGeolocationLayers(): Promise<ResponseValueObject<LayerEntity[]>> {
    const all = await this.getAll();
    if (!all.success || !all.data) {
      return new ResponseValueObject(false, []);
    }
    const geoLayers: LayerEntity[] = [];
    for (const item of all.data.operationalLayers) {
      if (item.layers) {
        for (const sub of item.layers) {
          if (sub.definitionQuery && sub.abbreviation) {
            geoLayers.push(sub);
          }
        }
      } else {
        if (item.definitionQuery && item.abbreviation) {
          geoLayers.push(item);
        }
      }
    }
    return new ResponseValueObject(true, geoLayers);
  }

  async getLayerMetadata(url: string): Promise<ResponseValueObject<any>> {
    try {
      const response = await axios.get(url, {
        params: { f: 'json' },
        timeout: 10_000,
      });
      return new ResponseValueObject(true, response.data);
    } catch (ex: any) {
      return new ResponseValueObject(false, null, ex.message);
    }
  }

  async getLegend(legendUrl: string, sublayerId?: string): Promise<ResponseValueObject<LegendItemValueObject[]>> {
    try {
      const response = await axios.get<{ layers?: { layerId: number; legend: LegendItemValueObject[] }[] }>(
        legendUrl,
        { timeout: 10_000 }
      );
      const layers = response.data?.layers ?? [];
      let items: LegendItemValueObject[];

      if (sublayerId !== undefined) {
        const match = layers.find(l => l.layerId?.toString() === sublayerId);
        items = match?.legend ?? [];
      } else {
        items = layers.flatMap(l => l.legend ?? []);
      }

      return new ResponseValueObject(true, items, undefined, []);
    } catch (ex: any) {
      return new ResponseValueObject(false, [], ex.message);
    }
  }

}
